import { Markdown } from '@Sage/hoi-poi-ui';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import { processFieldsForB4 } from 'utils/crud.js';
import { getActiveCrud } from 'utils/crud';

import { ANALYTICS } from 'constants/Entities';
import { publish } from 'lib/EventBuser';
import { REFRESH_TABLE } from 'lib/events';

import { getVisibleInOptions } from 'models/AnalyticsReportModel';
import { PROVIDERS_CONFIG, POWERBI_REPORT, PROVIDERS_OPTIONS } from 'constants/DashboardProviders';
import { getLiteralWithParameters } from '../../utils/getLiteral';
import { ensureRoute } from 'utils/routes';

export default class AnalyticsReportManager {
    processForServer = processFieldsForB4;

    getSchema = (defaultInputSchema) => {
        return [
            {
                title: getLiteral('label_info'),
                show: true,
                fields: [
                    {
                        ...defaultInputSchema,
                        id: 'title',
                        type: 'text',
                        mandatory: true,
                        label: getLiteral('label_analytics_pro_report_title'),
                        inputAttrs: {
                            forceFocus: true,
                        },
                    },
                    {
                        ...defaultInputSchema,
                        id: 'provider',
                        fieldConfiguration: 'provider',
                        type: 'singleValueList',
                        mandatory: true,
                        label: getLiteral('label_provider'),
                        inputAttrs: {
                            readOnly: true,
                            usePlainValue: true,
                            options: PROVIDERS_OPTIONS,
                        },
                    },
                    {
                        ...defaultInputSchema,
                        id: 'description',
                        type: 'textarea',
                        label: getLiteral('label_analytics_pro_report_description'),
                        inputAttrs: {
                            multiLine: true,
                            rows: 5,
                            rowsMax: 15,
                        },
                    },
                    {
                        ...defaultInputSchema,
                        id: 'displayOnWidget',
                        fieldConfiguration: 'displayOnWidget',
                        type: 'bool',
                        label: getLiteral('label_analytics_pro_report_display_widget_reports'),
                        description: getLiteral(
                            'label_analytics_pro_report_display_widget_reports_description',
                        ),
                        inputAttrs: {},
                    },
                    /*                     {
                        ...defaultInputSchema,
                        id: 'environment',
                        type: 'singleValueList',
                        label: getLiteral('label_analytics_pro_environment_visiblity'),
                        description: getLiteral(
                            'label_analytics_pro_environment_visiblity_description',
                        ),
                        inputAttrs: {
                            list: 'tblSucursales',
                        },
                    }, */
                    {
                        ...defaultInputSchema,
                        id: 'visibleIn',
                        fieldConfiguration: 'visibleIn',
                        type: 'multipleValueList',
                        label: getLiteral('label_analytics_pro_report_visible_entities'),
                        labelMode: 'vertical',
                        hint: getLiteral('label_selectone'),
                        inputAttrs: {
                            options: getVisibleInOptions(),
                        },
                    },
                ],
            },
            {
                title: getLiteral('label_metrics_external_provider_embedding_section'),
                show: true,
                fields: [
                    {
                        ...defaultInputSchema,
                        id: 'url',
                        fieldConfiguration: 'url',
                        type: 'url',
                        mandatory: true,
                        label: getLiteral('label_url'),
                        preAdvice: {
                            props: {
                                type: 'semanticInfo',
                                showIcon: true,
                                showCollapse: false,
                                title: getLiteral('label_metrics_how_to_get_embed_url'),
                            },
                            content: (
                                <Markdown
                                    overrides={{
                                        a: { type: 'caption', target: '_blank' },
                                    }}
                                    content={getLiteralWithParameters(
                                        'label_metrics_how_to_get_embed_url_desc',
                                        [
                                            'label_metrics_how_to_get_embed_url_powerbi_knowledge_base',
                                        ],
                                    )}
                                />
                            ),
                        },
                        validate: () => {
                            const state = Context.store.getState();
                            const activeCrud = getActiveCrud(state);
                            const crudData = activeCrud?.data;
                            return !PROVIDERS_CONFIG?.[crudData.provider]?.urlRegex?.test?.(
                                crudData.url,
                            )
                                ? getLiteral('label_metrics_external_provider_url_error')
                                : null;
                        },
                        inputAttrs: {},
                    },
                ],
            },
        ];
    };

    // Hide provider (and related widget fields) if data indicates POWERBI
    dataAffectsSchema = ({ data, schema }) => {
        const isPowerBI = data?.provider === POWERBI_REPORT;
        const newSchema = schema.map((tab) => ({
            ...tab,
            fields: tab.fields.map((field) => {
                if (field.id === 'provider') {
                    return { ...field, hidden: !isPowerBI };
                }
                if (field.id === 'displayOnWidget' || field.id === 'visibleIn') {
                    return { ...field, hidden: !!isPowerBI };
                }
                return field;
            }),
        }));
        return newSchema;
    };

    afterGetSchema = ({
        schema,
        dependencyMap,
        dynamicMap,
        smartFieldsMap,
        smartFieldsDependencyMap,
    }) => {
        // Configure dynamic visibility using dynamics system
        if (!dynamicMap.provider) dynamicMap.provider = {};

        // Show url only when provider is POWERBI
        dynamicMap.provider.url = [POWERBI_REPORT];

        // visibleIn depends on displayOnWidget being true
        if (!dynamicMap.displayOnWidget) dynamicMap.displayOnWidget = {};
        dynamicMap.displayOnWidget.visibleIn = [true];

        return Promise.resolve({
            schema,
            dependencyMap,
            dynamicMap,
            smartFieldsMap,
            smartFieldsDependencyMap,
        });
    };

    beforeSave = (_, data) => {
        if (data.url) {
            data.configuration = { url: data.url };
            data.url = undefined;
            data.provider = POWERBI_REPORT; // Because readonly, but we need to save the provider
        }
        if (!data.displayOnWidget) data.visibleIn = [];
        return data;
    };

    afterSave = (crud) => {
        if (!crud?.data?.openDetail || crud?.data?.id) return;
        ensureRoute(`/metrics/dashboards/view/${crud?.result?.code}`);
    };

    shouldOpenDetail = () => {
        return false;
    };

    customInitList = (data, id) => {
        if (data.openDetail) return;
        publish(`${REFRESH_TABLE}_${ANALYTICS.entity}`);
    };

    changeFields = (fields) => {
        let newFields = { ...fields };
        if (fields.displayOnWidget === false) {
            newFields.visibleIn = [];
        }
        return newFields;
    };
}
